import React, { useEffect, useMemo, useCallback } from "react";
import { useState } from "react";
import { classNames } from "../../../../../helpers/classNames";
import { BiHide } from "react-icons/bi";
import { ViewGridIcon, SearchIcon, RefreshIcon, EyeIcon, ArrowsExpandIcon } from "@heroicons/react/outline";
import { SortAscendingIcon, MinusIcon, PlusIcon } from "@heroicons/react/solid";
import ButtonDropdown from "../../../../ButtonDropdown";
import useArray from "../../../../../hooks/useArray";
import areArraysIdentical from "../../../../../helpers/arrayArraysIdentical";
import { useMutation, useQueryClient } from "react-query";
import { ApiRequest, API_AUTH_TYPES, API_ROUTES } from "../../../../../api";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ColumnOrderRow from './ColumnOrderRow';

const SmartTableHiddenFields = ({ columns, dbHiddenColumns, dbColumnOrder, dbColumnWidths, viewId, tableId, onHiddenColumnsChange, onColumnOrderChange, onColumnWidthsChange }) => {
  const hiddenColumns = useArray(dbHiddenColumns || []);
  const [columnOrder, setColumnOrder] = useState(dbColumnOrder || []);
  const [columnWidths, setColumnWidths] = useState(dbColumnWidths || {});
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showHiddenColumns, setShowHiddenColumns] = useState(false);

  // Get ordered columns - use custom order if available, otherwise use default order
  const orderedColumns = useMemo(() => {
    if (!columns || columns.length === 0) return [];

    if (columnOrder && columnOrder.length > 0) {
      // Create a map for quick lookup
      const columnMap = new Map(columns.map(col => [col.col_id, col]));
      const orderedCols = [];

      // Add columns in the specified order
      columnOrder.forEach(colId => {
        const column = columnMap.get(colId);
        if (column) {
          orderedCols.push(column);
          columnMap.delete(colId);
        }
      });

      // Add any remaining columns that weren't in the order array
      orderedCols.push(...Array.from(columnMap.values()));

      return orderedCols;
    }

    return columns;
  }, [columns, columnOrder]);

  // Filter columns based on search query and hidden toggle
  const filteredColumns = useMemo(() => {
    let columns = orderedColumns;

    // Filter out hidden columns if toggle is off
    if (!showHiddenColumns) {
      columns = columns.filter(column => !hiddenColumns.array.includes(column.col_id));
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      columns = columns.filter(column =>
        column.title.toLowerCase().includes(searchLower) ||
        column.col_id.toLowerCase().includes(searchLower) ||
        column.col_type.toLowerCase().includes(searchLower)
      );
    }

    return columns;
  }, [orderedColumns, searchQuery, showHiddenColumns, hiddenColumns.array]);

  useEffect(() => {
    hiddenColumns.set(dbHiddenColumns);
  }, [dbHiddenColumns]);

  useEffect(() => {
    setColumnOrder(dbColumnOrder || []);
  }, [dbColumnOrder]);

  useEffect(() => {
    setColumnWidths(dbColumnWidths || {});
  }, [dbColumnWidths]);

  // Check if any changes have been made
  const hasChanges = useMemo(() => {
    const hiddenChanged = !areArraysIdentical(hiddenColumns.array, dbHiddenColumns);
    const orderChanged = !areArraysIdentical(columnOrder, dbColumnOrder);
    const widthsChanged = JSON.stringify(columnWidths) !== JSON.stringify(dbColumnWidths || {});
    return hiddenChanged || orderChanged || widthsChanged;
  }, [hiddenColumns.array, dbHiddenColumns, columnOrder, dbColumnOrder, columnWidths, dbColumnWidths]);

  const viewsApi = new ApiRequest(
    API_ROUTES.SMART_TABLE_VIEWS,
    API_AUTH_TYPES.ADVISOR
  );

  const saveAllChanges = async () => {
    const promises = [];

    // Save hidden columns if changed
    if (!areArraysIdentical(hiddenColumns.array, dbHiddenColumns)) {
      promises.push(
        viewsApi.patch({
          endpoint: 'update',
          body: {
            column: "hidden_columns",
            value: hiddenColumns.array,
            id: viewId,
          }
        })
      );
    }

    // Save column order if changed
    if (!areArraysIdentical(columnOrder, dbColumnOrder)) {
      promises.push(
        viewsApi.patch({
          endpoint: 'update',
          body: {
            column: "column_order",
            value: columnOrder,
            id: viewId,
          }
        })
      );
    }

    // Save column widths if changed
    if (JSON.stringify(columnWidths) !== JSON.stringify(dbColumnWidths || {})) {
      promises.push(
        viewsApi.patch({
          endpoint: 'update',
          body: {
            column: "column_widths",
            value: columnWidths,
            id: viewId,
          }
        })
      );
    }

    // Execute all saves in parallel
    await Promise.all(promises);
  };

  const queryClient = useQueryClient();

  const { mutate: handleSaveAll, isLoading: isSaving } = useMutation(saveAllChanges, {
    onSuccess: () => {
      // Call all change handlers to update parent state
      if (onHiddenColumnsChange) {
        onHiddenColumnsChange(hiddenColumns.array);
      }
      if (onColumnOrderChange) {
        onColumnOrderChange(columnOrder);
      }
      if (onColumnWidthsChange) {
        onColumnWidthsChange(columnWidths);
      }


      // Invalidate the query for the smart table data
      queryClient.invalidateQueries(["smart-table-complete-data", { tableId }]);

    },
    onError: (error) => {
      console.error("Error saving column changes:", error);
    }
  });

  const handleToggleVisibility = useCallback((colId) => {
    if (hiddenColumns.array.includes(colId)) {
      hiddenColumns.remove(hiddenColumns.array.indexOf(colId));
    } else {
      hiddenColumns.push(colId);
    }
  }, [hiddenColumns]);

  const handleMoveColumn = useCallback((dragIndex, hoverIndex) => {
    const newOrder = [...orderedColumns.map(col => col.col_id)];
    const draggedColId = newOrder[dragIndex];
    newOrder.splice(dragIndex, 1);
    newOrder.splice(hoverIndex, 0, draggedColId);
    setColumnOrder(newOrder);
  }, [orderedColumns]);

  const handleWidthChange = useCallback((colId, width) => {
    setColumnWidths(prev => ({
      ...prev,
      [colId]: width
    }));
  }, []);

  const resetToDefaultOrder = () => {
    setColumnOrder([]);
  };

  const resetToDefaultWidths = () => {
    setColumnWidths({});
  };

  const resetVisibility = () => {
    hiddenColumns.set([]);
  };

  const adjustAllColumnWidths = (adjustment) => {
    const newWidths = {};
    orderedColumns.forEach(column => {
      const currentWidth = columnWidths[column.col_id] || column.default_width || 200;
      const newWidth = Math.max(100, currentWidth + adjustment);
      newWidths[column.col_id] = newWidth;
    });
    setColumnWidths(prev => ({ ...prev, ...newWidths }));
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div className="">
      <ButtonDropdown open={open} setOpen={setOpen}>
        <div className="p-3 min-w-[500px]">
          <div className="mb-3">
            <h3 className="text-base font-medium text-gray-900 mb-1">Column Management</h3>
            <p className="text-sm text-gray-500">Search, drag to reorder • Toggle visibility • Adjust widths • Toggle to show/hide hidden columns</p>
          </div>

          {/* Search Bar and Show Hidden Toggle - Combined Row */}
          <div className="mb-3 space-y-2">
            <div className="flex gap-3 items-start">
              {/* Search Bar */}
              <div className="flex-1">
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search columns by name, ID, or type..."
                    className="w-full pl-9 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Show Hidden Columns Toggle */}
              <div className={classNames(
                "flex-shrink-0 px-3 py-2.5 rounded-md border transition-colors duration-150",
                hiddenColumns.array.length > 0
                  ? "bg-amber-50 border-amber-200"
                  : "bg-gray-50 border-gray-200"
              )}>
                <div className="flex items-center space-x-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showHiddenColumns}
                      onChange={(e) => setShowHiddenColumns(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Show hidden</span>
                  </label>
                  {hiddenColumns.array.length > 0 && (
                    <span className={classNames(
                      "text-xs px-2 py-0.5 rounded-full",
                      "bg-amber-100 text-amber-800 font-medium"
                    )}>
                      {hiddenColumns.array.length}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Search Results Info */}
            {searchQuery && (
              <p className="text-xs text-gray-500 ml-1">
                Showing {filteredColumns.length} of {showHiddenColumns ? orderedColumns.length : orderedColumns.filter(col => !hiddenColumns.array.includes(col.col_id)).length} columns
              </p>
            )}
          </div>

          <DndProvider backend={HTML5Backend}>
            <div className="space-y-1 max-h-[300px] 2xl:max-h-[400px] overflow-y-auto">
              {filteredColumns.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-lg mb-2">No columns found</p>
                  <p className="text-sm">
                    {searchQuery ? (
                      <>
                        No columns match "{searchQuery}".{' '}
                        <button
                          onClick={clearSearch}
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          Clear search
                        </button>
                      </>
                    ) : !showHiddenColumns && hiddenColumns.array.length > 0 ? (
                      <>
                        All visible columns are shown above.{' '}
                        <button
                          onClick={() => setShowHiddenColumns(true)}
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          Show hidden columns
                        </button>{' '}
                        to see {hiddenColumns.array.length} hidden column{hiddenColumns.array.length !== 1 ? 's' : ''}.
                      </>
                    ) : (
                      'No columns available'
                    )}
                  </p>
                </div>
              ) : (
                filteredColumns.map((column, index) => (
                  <ColumnOrderRow
                    key={column.col_id}
                    column={column}
                    index={orderedColumns.findIndex(col => col.col_id === column.col_id)}
                    isHidden={hiddenColumns.array.includes(column.col_id)}
                    onToggleVisibility={handleToggleVisibility}
                    onMoveColumn={handleMoveColumn}
                    totalColumns={orderedColumns.length}
                    width={columnWidths[column.col_id]}
                    onWidthChange={handleWidthChange}
                  />
                ))
              )}
            </div>
          </DndProvider>

          <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-200">
            <div className="flex items-center space-x-4">
              {/* Order Controls */}
              <div className="flex items-center space-x-1">
                <div className="text-center">
                  <div className="text-xs font-medium text-gray-700 mb-1">Reset Order</div>
                  <button
                    onClick={resetToDefaultOrder}
                    className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors duration-150"
                    disabled={isSaving}
                    title="Reset column order to default"
                  >
                    <SortAscendingIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Width Controls */}
              <div className="flex items-center space-x-1">
                <div className="text-center">
                  <div className="text-xs font-medium text-gray-700 mb-1">Column Widths</div>
                  <div className="flex space-x-0.5">
                    <button
                      onClick={resetToDefaultWidths}
                      className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors duration-150"
                      disabled={isSaving}
                      title="Reset all column widths to default"
                    >
                      <RefreshIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => adjustAllColumnWidths(-50)}
                      className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors duration-150"
                      disabled={isSaving}
                      title="Decrease all column widths by 50px"
                    >
                      <MinusIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => adjustAllColumnWidths(50)}
                      className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors duration-150"
                      disabled={isSaving}
                      title="Increase all column widths by 50px"
                    >
                      <PlusIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Visibility Controls */}
              <div className="flex items-center space-x-1">
                <div className="text-center">
                  <div className="text-xs font-medium text-gray-700 mb-1">Show All</div>
                  <button
                    onClick={resetVisibility}
                    className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors duration-150"
                    disabled={isSaving}
                    title="Show all hidden columns"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Search Controls */}
              {searchQuery && (
                <div className="flex items-center space-x-1">
                  <div className="text-center">
                    <div className="text-xs font-medium text-gray-700 mb-1">Clear Search</div>
                    <button
                      onClick={clearSearch}
                      className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors duration-150"
                      title="Clear search"
                    >
                      <RefreshIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={handleSaveAll}
              disabled={isSaving || !hasChanges}
              className={classNames(
                'inline-flex items-center px-4 py-2 text-sm font-medium border rounded-md transition-colors duration-150',
                isSaving ? 'bg-gray-200 text-gray-500 cursor-not-allowed border-gray-200' :
                  hasChanges ? 'bg-slate-600 hover:bg-slate-700 text-white border-slate-600 animate-pulse' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
              )}
            >
              {isSaving ? 'Saving...' : (hasChanges ? 'Save Changes' : 'Changes Saved')}
            </button>
          </div>
        </div>
      </ButtonDropdown>

      <button
        onClick={() => setOpen(true)}
        className={classNames(
          "rounded hover:ring-2 hover:ring-gray-300 text-gray-600 px-2 py-1 flex space-x-1 items-center",
          (hiddenColumns.array.length > 0 || hasChanges) && "bg-slate-100",
          hasChanges && "ring-2 ring-slate-400 ring-offset-1"
        )}
      >
        <ViewGridIcon className="h-5 w-5" />
        <p className="hidden xl:flex">
          {hiddenColumns.array.length === 0
            ? "Columns"
            : `${hiddenColumns.array.length} hidden`}
          {hasChanges && !isSaving && ' *'}
        </p>
      </button>
    </div>
  );
};

export default SmartTableHiddenFields;
